import type { FlowEvent, FlowState, Step } from './types';
import { createInitialState } from './types';

function cardStepBeforeCategories(state: FlowState): Step {
  return state.matchCandidates?.length ? 'matchCheck' : 'reviewCard';
}

function previousFormStep(state: FlowState): Step {
  if (state.method === 'qr') return 'qrScan';
  return state.method === 'card' ? cardStepBeforeCategories(state) : 'contactForm';
}

function createFailureStep(state: FlowState): Step {
  if (state.method === 'qr') return 'qrScan';
  if (state.method === 'card') return cardStepBeforeCategories(state);
  return 'reviewSave';
}

function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

function applyPreset(state: FlowState, ids: string[]): FlowState {
  if (sameIds(state.presetCategoryIds, ids)) return state;
  const untouched = state.categoryIds === state.presetCategoryIds;
  return { ...state, presetCategoryIds: ids, categoryIds: untouched ? ids : state.categoryIds };
}

export function reducer(state: FlowState, event: FlowEvent): FlowState {
  if (event.type === 'RESET') {
    return createInitialState(state.presetCategoryIds, event.startWithManualEntry);
  }
  if (event.type === 'PRESET_CATEGORIES') return applyPreset(state, event.ids);

  switch (state.step) {
    case 'methodSelect':
      switch (event.type) {
        case 'PICK_QR':
          return { ...state, step: 'qrScan', method: 'qr' };
        case 'PICK_CARD':
          return { ...state, step: 'cardUpload', method: 'card' };
        case 'PICK_MANUAL':
          return { ...state, step: 'contactForm', method: 'manual' };
        default:
          return state;
      }
    case 'qrScan':
      switch (event.type) {
        case 'CODE_CONSUMED':
          return { ...state, pendingCode: undefined };
        case 'SCANNED':
          return {
            ...state,
            step: event.chooseCategories ? 'categorySelect' : 'creating',
            data: event.data,
            qrReferenceId: event.referenceId,
            qrContactCode: event.contactCode,
          };
        case 'BACK':
          return { ...state, step: 'methodSelect' };
        default:
          return state;
      }
    case 'cardUpload':
      switch (event.type) {
        case 'EXTRACT_OK':
          return { ...state, step: 'reviewCard', data: { ...state.data, ...event.data } };
        case 'BACK':
          return { ...state, step: 'methodSelect' };
        default:
          return state;
      }
    case 'reviewCard':
      switch (event.type) {
        case 'REVIEW_CONTINUE': {
          const next = {
            ...state,
            data: { ...state.data, ...event.data },
            matchCandidates: event.candidates,
          };
          if (event.candidates.length > 0) return { ...next, step: 'matchCheck' };
          return { ...next, step: event.skipCategories ? 'creating' : 'categorySelect' };
        }
        case 'BACK':
          return { ...state, step: 'cardUpload' };
        default:
          return state;
      }
    case 'matchCheck':
      switch (event.type) {
        case 'MATCH_RESOLVED':
          if (event.contactId) {
            return { ...state, step: 'merging', mergeContactId: event.contactId };
          }
          return { ...state, step: event.skipCategories ? 'creating' : 'categorySelect' };
        case 'BACK':
          return { ...state, step: 'reviewCard' };
        default:
          return state;
      }
    case 'merging':
      switch (event.type) {
        case 'MERGE_OK':
          return { ...state, step: 'closed' };
        case 'MERGE_FAIL':
          return { ...state, step: 'matchCheck' };
        default:
          return state;
      }
    case 'contactForm':
      switch (event.type) {
        case 'SUBMIT':
          return { ...state, step: 'categorySelect', data: { ...state.data, ...event.data } };
        case 'SUBMIT_DIRECT':
          return {
            ...state,
            step: 'reviewSave',
            data: { ...state.data, ...event.data },
            categoryIds: state.presetCategoryIds,
            skippedCategories: true,
          };
        case 'CREATE_ORG':
          return {
            ...state,
            step: 'orgCreate',
            orgCreateName: event.name,
            data: event.formSnapshot,
          };
        case 'BACK':
          return {
            ...state,
            step: state.method === 'manual' ? 'methodSelect' : 'cardUpload',
          };
        default:
          return state;
      }
    case 'orgCreate':
      switch (event.type) {
        case 'BACK':
          return { ...state, step: 'contactForm' };
        case 'ORG_CREATED':
          return {
            ...state,
            step: 'contactForm',
            data: { ...state.data, organizationId: event.orgId, organizationName: event.orgName },
          };
        default:
          return state;
      }
    case 'categorySelect':
      switch (event.type) {
        case 'FINISH_CATEGORIES':
          return {
            ...state,
            step: state.method === 'manual' ? 'reviewSave' : 'creating',
            categoryIds: event.ids,
            skippedCategories: false,
          };
        case 'SKIP_CATEGORIES':
          return {
            ...state,
            step: state.method === 'manual' ? 'reviewSave' : 'creating',
            skippedCategories: false,
          };
        case 'BACK':
          return { ...state, step: previousFormStep(state) };
        default:
          return state;
      }
    case 'reviewSave':
      switch (event.type) {
        case 'SAVE_REVIEWED':
          return { ...state, step: 'creating', data: { ...state.data, notes: event.notes } };
        case 'EDIT_CATEGORIES':
          return { ...state, step: 'categorySelect' };
        case 'BACK':
          return { ...state, step: state.skippedCategories ? 'contactForm' : 'categorySelect' };
        default:
          return state;
      }
    case 'creating':
      switch (event.type) {
        case 'CREATE_OK':
          return {
            ...state,
            step: state.method === 'card' ? 'closed' : 'success',
            createdContactId: event.contactId,
            createdContactIsNew: event.isNew,
          };
        case 'CREATE_FAIL':
          return { ...state, step: createFailureStep(state) };
        default:
          return state;
      }
    case 'success':
      switch (event.type) {
        case 'FINISH':
          return { ...state, step: 'closed' };
        default:
          return state;
      }
    default:
      return state;
  }
}
